import { getMomentTz, MESApiService, spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import type { DelayByShift } from "../models/DelayByShift";
import type { ProductionEventOption } from "../models/ProductionEventOption";

const moment = getMomentTz();

type getDelaysProps = {
	startDate: Date;
	endDate: Date;
	delayAreaAssetId?: number | null;
	includeMicrodelays: boolean;
	shift?: string;
	crew?: string;
	delayCodeId?: number | null;
	delayCategoryId?: number | null;
	sectionId?: number | null;
	productionEventId?: number | null;
	productionEventMillAssetId?: number | null;
};

type getProductionEventsBySectionProps = {
	sectionId: number;
	delayAreaAssetId?: number | null;
};

export const getDelays = async (
	params: getDelaysProps,
	signal?: AbortSignal,
) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "StartDate", value: params.startDate.toISOString() },
		{ name: "EndDate", value: params.endDate.toISOString() },
		{ name: "DelayAreaAssetId", value: params.delayAreaAssetId ?? null },
		{
			name: "Shift",
			value: params.shift === "all" ? null : (params.shift ?? null),
		},
		{
			name: "Crew",
			value: params.crew === "all" ? null : (params.crew ?? null),
		},
		{ name: "IncludeMicrodelays", value: params.includeMicrodelays },
		{ name: "DelayCodeId", value: params.delayCodeId ?? null },
		{ name: "DelayCategoryId", value: params.delayCategoryId ?? null },
		{ name: "SectionId", value: params.sectionId ?? null },
		{ name: "ProductionEventId", value: params.productionEventId ?? null },
		{
			name: "ProductionEventMillAssetId",
			value: params.productionEventMillAssetId ?? null,
		},
	];

	const resp = await apiService.callV2("[DEL].[GetDelays]", parameters, signal);

	if (resp.ok) {
		let rows = get(resp, "data.tables[0].rows", []) as DelayByShift[];
		rows = rows.map((row) => ({
			...row,
			Start: row.Start ? moment.utc(row.Start).toDate() : null,
			End: row.End ? moment.utc(row.End).toDate() : null,
			OriginalStart: row.OriginalStart
				? moment.utc(row.OriginalStart).toDate()
				: null,
			OriginalEnd: row.OriginalEnd
				? moment.utc(row.OriginalEnd).toDate()
				: null,
			JustifiedDatetime: row.JustifiedDatetime
				? moment.utc(row.JustifiedDatetime).toDate()
				: null,
		}));

		return rows;
	} else {
		throw new Error(resp.message || "Error fetching delays");
	}
};

export const getProductionEventsBySection = async (
	params: getProductionEventsBySectionProps,
	signal?: AbortSignal,
) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "SectionId", value: params.sectionId },
		{ name: "DelayAreaAssetId", value: params.delayAreaAssetId ?? null },
	];

	const resp = await apiService.callV2(
		"[DEL].[GetProductionEventsBySection]",
		parameters,
		signal,
	);

	if (resp.ok) {
		const rows = get(
			resp,
			"data.tables[0].rows",
			[],
		) as ProductionEventOption[];

		return rows.map((row) => ({
			...row,
			Start: moment.utc(row.Start).toDate(),
			End: row.End ? moment.utc(row.End).toDate() : null,
		}));
	} else {
		throw new Error(resp.message || "Error fetching production events");
	}
};

export const getShifts = async (signal?: AbortSignal) => {
	const apiService = new MESApiService();

	const resp = await apiService.callV2("[MES].[GetShiftsAndCrews]", [], signal);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as String[];
	} else {
		throw new Error(resp.message || "Error fetching shifts");
	}
};

export const getCrews = async (signal?: AbortSignal) => {
	const apiService = new MESApiService();

	const resp = await apiService.callV2("[MES].[GetShiftsAndCrews]", [], signal);

	if (resp.ok) {
		return get(resp, "data.tables[1].rows", []) as String[];
	} else {
		throw new Error(resp.message || "Error fetching crews");
	}
};
